import { useEffect, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { tierOptions } from '../lanes';
import { orgUnitTypesApi, orgUnitsApi } from '../organizationApi';
import { sampleOrgUnit } from '../utils/sampleOrgUnit';
import type { OrgUnitFormSource, OrgUnitOption, OrgUnitTypeOption } from '../organizationTypes';

/** The value the parent picker uses for "this is a top-level unit". */
const ROOT = '__root__';

/**
 * Create or edit one node of the chart.
 *
 * Both pickers are fed from the server's own option feeds rather than from the list this
 * modal was opened over: the parent feed drops the unit's own branch, which is what stops
 * the control offering a parent the server would reject as a cycle.
 *
 * The field order follows the decisions a reader actually makes, in order: what it is called,
 * what tier it sits at, where it hangs, then its identifiers, then the prose. Code and Order
 * share a row because both are short machine values about the same thing — where this unit
 * sorts and what it is referred to as.
 */
export function OrgUnitModal({
  open,
  editing,
  defaultParent = null,
  suggestCode,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. Structural, so the tree node and a list row both satisfy it. */
  editing: OrgUnitFormSource | null;
  /**
   * The unit this one is being added under — what "Add child unit" passes.
   *
   * **It carries the name, not just the id, and that is the point.** The picker resolves its
   * label by finding the value among its options, and those arrive from the server a moment
   * later; seeded with an id alone the field renders its placeholder until they land, so a
   * create opened from a node looked as though no parent was set at all. The name is injected
   * as an option immediately, so the field states the right parent from the first paint.
   *
   * **It locks the field.** The reader picked the parent by choosing whose menu to open, so
   * the picker has nothing left to ask and every answer it could accept would contradict the
   * gesture that opened it. Create on the toolbar is the way in when the parent is the thing
   * being decided. Ignored on an edit, where the unit's own parent is the truth.
   */
  defaultParent?: { id: string; name: string; tierSortOrder: number } | null;
  /**
   * The code to offer for a new unit under a given parent.
   *
   * Supplied by the caller because the caller holds the whole chart: the suggestion is the
   * next free sibling under that parent, which cannot be derived from the unit being created.
   */
  suggestCode?: (parentId: string | null) => string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const canCreateType = usePermission('org-unit-types.create');
  /*
    Read out as a primitive so the fetch effect can depend on it honestly. The object it
    comes from is stable for as long as this modal is mounted — the parent opens it once per
    create — so declaring it costs no extra request and the effect stops reading a value it
    never declared.
  */
  const parentTier = defaultParent?.tierSortOrder;

  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [codeInput, setCodeInput] = useState(editing?.code ?? '');
  const [shortCode, setShortCode] = useState(editing?.shortCode ?? '');
  /*
    An edit starts "touched": the unit already has a code, and quietly replacing a stored
    value with a suggestion would change data the reader never asked to change.
  */
  const [codeTouched, setCodeTouched] = useState(editing !== null);
  const [typeId, setTypeId] = useState<string | null>(editing?.orgUnitTypeId ?? null);
  const [parentId, setParentId] = useState<string>(
    editing?.parentId ?? defaultParent?.id ?? ROOT,
  );
  const [description, setDescription] = useState(editing?.description ?? '');

  const [types, setTypes] = useState<OrgUnitTypeOption[]>([]);
  const [parents, setParents] = useState<OrgUnitOption[]>([]);
  // Starts true: the fetch is already in flight by first paint, so a false start would
  // flash an empty picker.
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // Fetch only — the state above is already correct for this open.
  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      orgUnitTypesApi.options(controller.signal),
      orgUnitsApi.options(editing?.id, controller.signal),
    ])
      .then(([loadedTypes, loadedParents]) => {
        setTypes(loadedTypes);
        setParents(loadedParents);

        /*
          The tier defaults to the rung this unit belongs on, so the form opens answerable
          rather than with a required field already in error.

          Under a parent that is the first tier BELOW the parent's — the only position a
          child can take if the ladder is to mean anything, and choosing it here is what
          stops a chart growing columns that own no tier. With no parent it is the first
          tier, which is also the whole answer on an empty chart.

          Set functionally, and only when nothing is chosen: the fetch resolves after mount,
          and it must never overwrite a tier the reader picked while it was in flight.
        */
        if (!editing) {
          const ladder = [...loadedTypes].sort((a, b) => a.sortOrder - b.sortOrder);
          const floor = parentTier;
          const next =
            floor === undefined
              ? ladder[0]
              : (ladder.find((t) => t.sortOrder > floor) ?? undefined);

          if (next) setTypeId((current) => current ?? next.id);
        }
      })
      .catch((caught) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        const failure = caught instanceof ApiError ? caught : new ApiError('Could not load the pickers.', 0);

      setError(failure);
      // No focus call: a failed load names no field, so there is nothing to point at.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingOptions(false);
      });

    return () => controller.abort();
  }, [editing, parentTier]);

  /*
    Derived, never an effect: the suggestion follows the chosen parent, so changing the
    parent re-offers a code under the new one. The moment the reader types in the field it
    stops following, because from then on the value is theirs.
  */
  const suggestion = suggestCode?.(parentId === ROOT ? null : parentId) ?? '';
  const code = codeTouched ? codeInput : suggestion;

  /*
    Locked, not merely seeded — the form was opened from a specific node's own menu. Disabled
    for STATE rather than permission, so it stays visible and says why in its own help text:
    a hidden field would leave the reader unable to see where the unit is going at all.
  */
  const parentLocked = editing === null && defaultParent !== null;

  const valid = name.trim().length > 0 && Boolean(typeId);
  const canSave = valid && confirmed && !saving;

  /*
    A deleted type stays selectable only where it already was. The server enforces this; the
    picker shows it so the field does not read as blank on a unit that has one.
  */
  const typeOptions = tierOptions(types);
  const editingTypeMissing = editing && typeId && !types.some((t) => t.id === typeId);

  if (editingTypeMissing) {
    typeOptions.push({
      value: editing.orgUnitTypeId,
      label: `${editing.orgUnitTypeName} (deleted)`,
    });
  }

  /*
    The seeded parent, guaranteed present in the list from the first paint even while the
    feed is still in flight — and de-duplicated, because once the feed lands it holds this
    unit too and the picker would otherwise offer the same parent twice.
  */
  const parentOptions = [
    { value: ROOT, label: 'None — this is a top-level unit' },
    ...(defaultParent && !editing && !parents.some((p) => p.id === defaultParent.id)
      ? [{ value: defaultParent.id, label: defaultParent.name }]
      : []),
    ...parents.map((p) => ({ value: p.id, label: p.name, right: p.orgUnitTypeName })),
  ];

  /**
   * Fills the one field the reader has to supply — the name.
   *
   * Not the tier, which already defaults to the right rung. Not the code: it follows the
   * parent's path on its own until the reader types, so writing one here would only switch
   * that off. Not the parent, which is already decided. And not the description, which is a
   * rich-text document the editor owns after mount — setting the state behind it would leave
   * the value and what is on screen disagreeing.
   */
  function autogenerate() {
    const sample = sampleOrgUnit();

    setName(sample.name);

    // Unlike the path code beside it, nothing derives this one — so there is no suggestion
    // for a generated value to fight, and leaving it empty would make every generated unit
    // unusable as a document's originating unit in the Repository.
    setShortCode(sample.shortCode);

    // Resolved against the live catalog rather than invented: an id this form made up would
    // fail the server's "the type must exist and be active" check.
    if (!typeId && types.length > 0) {
      setTypeId(types[0].id);
    }
  }

  async function save() {
    if (!canSave || !typeId) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      code: code.trim() || null,
      shortCode: shortCode.trim() || null,
      orgUnitTypeId: typeId,
      parentId: parentId === ROOT ? null : parentId,
      description: description.trim() || null,
    };

    try {
      if (editing) await orgUnitsApi.update(editing.id, request);
      else await orgUnitsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the unit.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, {
        idPrefix: 'org-unit',
        // Two pickers named for the thing chosen rather than for the id sent.
        fieldIds: { orgUnitTypeId: 'org-unit-type', parentId: 'org-unit-parent' },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit unit' : 'New unit'}
      /*
        Wide, because the description is a document: a rich-text field with a table in it is
        unreadable in the column a short form wants, and the toolbar wraps to two rows.
      */
      size="2xl"
      id="org-unit-modal"
      /*
        Create only. On an edit this would overwrite the unit's real name and order with test
        data — the one thing a form over existing data must never do.
      */
      autogenerate={
        editing === null
          ? { onGenerate: autogenerate, disabled: saving || loadingOptions }
          : undefined
      }
      confirm={{
        id: 'org-unit-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="org-unit-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="org-unit-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="org-unit-name"
          label="Name"
          required
          description="Unique among the active units sharing this parent. The same name may appear elsewhere in the chart."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={150}
          disabled={saving}
        />

        {/*
          Two decisions about placement, side by side: what tier this unit is, and what it
          hangs under. They read together, and the shared inputs are exactly one label line
          tall, so the row aligns by construction.
        */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            id="org-unit-type"
            label="Tier"
            required
            remote
            loading={loadingOptions}
            description="The tier this unit sits at. Type a name that is not listed to add it to the catalog without leaving this form."
            value={typeId}
            onChange={setTypeId}
            options={typeOptions}
            // Already ordered by level — see `tierOptions`. Sorting again scrambles the ladder.
            sort="none"
            error={typeId ? undefined : 'Choose a tier.'}
            disabled={saving}
            /*
              Inline creation, gated on the catalog's own create key — a caller who may not
              add a tier is offered no Add row at all, rather than one that 403s. The form
              renders INSIDE the picker's panel, never as a second modal: a dialog stacked on
              this one would break the product's one-modal rule and put the tier form on top
              of the unit form the reader is still filling in.
            */
            allowCustom={canCreateType && !saving}
            renderCreateForm={
              canCreateType
                ? (raw, close) => (
                    <NewUnitTypeForm
                      initialName={raw}
                      onCancel={close}
                      onCreated={(created) => {
                        setTypes((current) => [...current, created]);
                        setTypeId(created.id);
                        close();
                      }}
                    />
                  )
                : undefined
            }
          />

          <Select
            id="org-unit-parent"
            label="Parent unit"
            remote
            loading={loadingOptions}
            clearable={false}
            /*
              A disabled control has to say why — so the help text carries the reason and the
              way round it, rather than leaving a field the reader cannot move and cannot
              account for.
            */
            description={
              parentLocked
                ? `Fixed: this unit is being added under "${defaultParent?.name}". Use Create on the toolbar to choose the parent instead.`
                : "Where this unit hangs. A unit's own branch is not offered, because a unit cannot sit under itself or anything below it."
            }
            value={parentId}
            onChange={(next) => setParentId(next ?? ROOT)}
            options={parentOptions}
            disabled={saving || parentLocked}
          />
        </div>

        <TextField
          id="org-unit-code"
          label="Code"
          description="Optional short reference. Suggested from the parent's code as the next free number under it, until you type your own."
          value={code}
          onChange={(e) => {
            setCodeTouched(true);
            setCodeInput(e.target.value);
          }}
          maxLength={40}
          disabled={saving}
        />

        {/*
          A SECOND code, and the two mean different things — which is why the help text on
          each says so rather than leaving a reader to work out why one field is "Code" and
          the next is "Business unit code".

          The one above is a path (0001-2-3) encoding where the unit sits. This one is the
          mnemonic a repository reference number is built from: L2-SAL-26-001. Set it on the
          units that ARE business units and leave it empty further down — a document filed
          under a unit without one resolves to the nearest ancestor that has one.
        */}
        <TextField
          id="org-unit-short-code"
          label="Business unit code"
          description="Optional, 2–5 letters, e.g. SAL or ITOPS. Used as the business-unit segment of a document's reference number in the Repository — L2-SAL-26-001. Set it on divisions and leave it empty below them: a document filed under a unit without one takes the nearest ancestor's. SYS is reserved."
          value={shortCode}
          onChange={(e) => setShortCode(e.target.value.toUpperCase())}
          maxLength={5}
          disabled={saving}
        />

        <LazyRichTextField
          id="org-unit-description"
          label="Description"
          description="What this unit does. Formatting, lists and tables are kept; anything the toolbar cannot produce is removed when it is saved."
          value={description}
          onChange={setDescription}
          disabled={saving}
          placeholder="What this unit is responsible for…"
        />
      </div>
    </Modal>
  );
}

/**
 * Add a tier to the catalog from inside the unit form's own picker.
 *
 * **The name only.** A tier's position in the ladder is the server's to assign — it appends
 * to the end — and everything else about a tier is edited in Organization settings. Asking
 * for more here would turn a two-second detour into a second form.
 */
function NewUnitTypeForm({
  initialName,
  onCancel,
  onCreated,
}: {
  initialName: string;
  onCancel: () => void;
  onCreated: (created: OrgUnitTypeOption) => void;
}) {
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = name.trim().length > 0 && !saving;

  async function create() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const created = await orgUnitTypesApi.create({
        name: name.trim(),
        description: null,
      });

      onCreated({
        id: created.id,
        name: created.name,
        sortOrder: created.sortOrder,
        // The server stamped it on the way back, so the picker shows the new tier's real
        // position without refetching the ladder.
        level: created.level,
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not add the tier.');
    } finally {
      setSaving(false);
    }
  }

  return (
    /*
      Keystrokes stop here. The panel sits inside the unit modal, so an un-swallowed Enter
      would reach the form behind it and a Escape would close the whole modal rather than
      this strip — the reader would lose the unit they were part-way through describing.
    */
    <div
      className="flex flex-col gap-2 p-3"
      onKeyDown={(e) => {
        e.stopPropagation();

        if (e.key === 'Enter') {
          e.preventDefault();
          void create();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
    >
      <p className="text-xs font-medium text-slate-700">New tier</p>

      {error ? (
        <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
          {error}
        </p>
      ) : null}

      <TextField
        id="org-unit-type-inline-name"
        label="Name"
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={150}
        disabled={saving}
      />

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          id="org-unit-type-inline-cancel"
          variant="secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          id="org-unit-type-inline-save"
          variant="primary"
          onClick={() => void create()}
          disabled={!canSave}
          loading={saving}
          loadingLabel="Submitting"
        >
          Submit
        </Button>
      </div>
    </div>
  );
}
